// Helpers MFA TOTP — enroll / verify / list / unenroll.
// Supabase Auth supporte les facteurs TOTP nativement (aal1 → aal2).

import { supabase } from '../lib/supabase'

export interface TotpEnrollment {
  factor_id: string
  /** SVG du QR code à scanner avec une app TOTP. */
  qr_code: string
  /** Secret texte (si l'utilisateur ne peut pas scanner). */
  secret: string
  /** URI otpauth://… (alternative). */
  uri: string
}

export interface MfaFactor {
  id: string
  factor_type: 'totp' | 'phone'
  status: 'verified' | 'unverified'
  friendly_name?: string | null
  created_at: string
}

export const enrollTotp = async (friendlyName: string): Promise<TotpEnrollment> => {
  const { data, error } = await supabase.auth.mfa.enroll({
    factorType: 'totp',
    friendlyName,
  })
  if (error) throw error
  return {
    factor_id: data.id,
    qr_code: data.totp.qr_code,
    secret: data.totp.secret,
    uri: data.totp.uri,
  }
}

export const verifyTotpEnroll = async (factorId: string, code: string): Promise<void> => {
  // Pour finaliser l'enrôlement il faut un challenge
  const { data: chal, error: chalError } = await supabase.auth.mfa.challenge({
    factorId,
  })
  if (chalError) throw chalError
  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: chal.id,
    code,
  })
  if (error) throw error
}

export const listFactors = async (): Promise<MfaFactor[]> => {
  const { data, error } = await supabase.auth.mfa.listFactors()
  if (error) throw error
  // Combine totp + phone factors
  return [...(data.totp ?? []), ...(data.phone ?? [])] as MfaFactor[]
}

export const unenrollFactor = async (factorId: string): Promise<void> => {
  const { error } = await supabase.auth.mfa.unenroll({ factorId })
  if (error) throw error
}

/**
 * Crée un challenge + vérifie le code (utilisé pendant le login flow pour
 * passer de AAL1 à AAL2).
 */
export const challengeAndVerify = async (factorId: string, code: string): Promise<void> => {
  const { data: chal, error: chalError } = await supabase.auth.mfa.challenge({
    factorId,
  })
  if (chalError) throw chalError
  const { error } = await supabase.auth.mfa.verify({
    factorId,
    challengeId: chal.id,
    code,
  })
  if (error) throw error
}

export interface AalStatus {
  current: 'aal1' | 'aal2' | null
  nextRequired: 'aal1' | 'aal2' | null
}

export const getAalStatus = async (): Promise<AalStatus> => {
  const { data, error } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel()
  if (error) throw error
  return {
    current: (data.currentLevel as AalStatus['current']) ?? null,
    nextRequired: (data.nextLevel as AalStatus['nextRequired']) ?? null,
  }
}
